/**
 * Admin Articles List
 *
 * Displays drafts + published guides for Tammy.
 * Uses backend admin endpoint: GET /api/articles/admin
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HiPlus,
  HiSearch,
  HiPencil,
  HiTrash,
  HiEye,
  HiEyeOff,
  HiRefresh,
} from 'react-icons/hi';
import { adminFetch, adminHeaders } from '../lib/adminApi';

type Article = {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  status: 'draft' | 'published';
  published_at: string | null;
  updated_at: string;
};

type StatusFilter = 'all' | 'draft' | 'published';

export default function Articles() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const fetchArticles = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('status', statusFilter);
      if (searchTerm.trim()) params.set('search', searchTerm.trim());
      params.set('limit', '200');

      const res = await adminFetch(`/api/articles/admin?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to load articles');
      const data = await res.json();
      setArticles(data.articles || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return articles;
    return articles.filter((a) => {
      const hay = `${a.title} ${a.slug} ${a.excerpt || ''}`.toLowerCase();
      return hay.includes(term);
    });
  }, [articles, searchTerm]);

  const togglePublish = async (id: string, publish: boolean) => {
    try {
      const res = await adminFetch(`/api/articles/admin/${id}/publish`, {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ publish }),
      });
      if (!res.ok) throw new Error('Failed to update publish status');
      await fetchArticles();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update publish status');
    }
  };

  const deleteArticle = async (id: string) => {
    const ok = window.confirm('Delete this article? This cannot be undone.');
    if (!ok) return;
    try {
      const res = await adminFetch(`/api/articles/admin/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Failed to delete article');
      setArticles((prev) => prev.filter((a) => a.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete article');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Articles</h1>
          <p className="text-gray-600 mt-1">Create and publish guides for your website.</p>
        </div>
        <Link
          to="/admin/articles/new"
          className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700
                     text-white rounded-xl font-semibold transition-colors"
        >
          <HiPlus className="w-5 h-5" />
          New Article
        </Link>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1 flex items-center gap-3 border border-gray-200 rounded-xl px-3 py-2">
            <HiSearch className="w-5 h-5 text-gray-400" />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search title or slug..."
              className="w-full outline-none text-gray-900 placeholder-gray-400"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="border border-gray-200 rounded-xl px-3 py-2 bg-white text-gray-900"
          >
            <option value="all">All</option>
            <option value="draft">Drafts</option>
            <option value="published">Published</option>
          </select>

          <button
            onClick={fetchArticles}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200
                       rounded-xl text-gray-700 font-medium transition-colors"
          >
            <HiRefresh className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 border border-red-200 rounded-2xl p-4">
          {error}
        </div>
      )}

      {loading && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 text-gray-600">
          Loading articles...
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 text-gray-600">
          No articles found.
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Title</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 font-semibold">Slug</th>
                  <th className="text-right px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{a.title}</div>
                      {a.excerpt && <div className="text-gray-500 line-clamp-1">{a.excerpt}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={
                          a.status === 'published'
                            ? 'inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-50 text-green-700'
                            : 'inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700'
                        }
                      >
                        {a.status === 'published' ? 'Published' : 'Draft'}
                      </span>
                      {a.status === 'published' && a.published_at && (
                        <div className="text-xs text-gray-500 mt-1">
                          {new Date(a.published_at).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700 font-mono text-xs">{a.slug}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => togglePublish(a.id, a.status !== 'published')}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700"
                          title={a.status === 'published' ? 'Unpublish' : 'Publish'}
                        >
                          {a.status === 'published' ? (
                            <HiEyeOff className="w-4 h-4" />
                          ) : (
                            <HiEye className="w-4 h-4" />
                          )}
                        </button>

                        <Link
                          to={`/admin/articles/${a.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-purple-50 hover:bg-purple-100 text-purple-700"
                          title="Edit"
                        >
                          <HiPencil className="w-4 h-4" />
                        </Link>

                        <button
                          onClick={() => deleteArticle(a.id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-700"
                          title="Delete"
                        >
                          <HiTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


